import { FC, useState } from "react";

import {
  ServiceTaskTypeEnum,
  TaskDriverStatusEnum,
} from "@/domain/models/common";
import { useTranslate } from "@/i18n/useTranslate";

import {
  CameraPhotoWithCommentType,
  DriverCamera,
  DriverFlowBottomButton,
} from "../blocks";
import { DsText } from "../components/ui";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUploadServicePhotoMutation } from "../data/mutations/storageMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { JobServiceTaskBaseType } from "../domain/task";
import { DriverTopActionButton } from "./DriverTopActionButton";

const DriverAddPhotoBinContents: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const { mutate: uploadServicePhoto, isPending: isUploadingServicePhoto } =
    useUploadServicePhotoMutation();

  const isDumpAndReturnBinTask =
    taskId === ServiceTaskTypeEnum.DUMPANDRETURNBIN;

  const {
    mutate: updateService,
    isPending: isUpdatingService,
    variables,
  } = useUpdateServiceMutation();
  const { mutate: updateTask, isPending: isUpdatingTask } =
    useUpdateTaskMutation();

  const [isCameraOpen, setIsCameraOpen] = useState(false);

  const openCamera = () => setIsCameraOpen(true);

  const closeCamera = () => setIsCameraOpen(false);

  const uploadPhoto = ({
    base64PhotoString,
    comment,
  }: CameraPhotoWithCommentType) => {
    uploadServicePhoto(
      {
        jobId,
        serviceId,
        base64PhotoString,
        baseName: "binMaterialsPhoto",
      },
      {
        onSuccess: path => {
          const driverFlowStatus = isDumpAndReturnBinTask
            ? TaskDriverStatusEnum.DRIVING_TO_DUMP
            : TaskDriverStatusEnum.TASK_REVIEW;

          updateService({
            jobId,
            serviceId,
            binMaterialsPhoto: {
              photoUrl: path,
              comment,
            },
            turnaroundPhoto: null,
            driverFlowStatus,
          });

          updateTask({
            jobId,
            serviceId,
            taskId,
            driverFlowStatus,
          });
        },
      },
    );
  };

  const isLoading = isUpdatingService || isUpdatingTask;

  const isNextButtonLoading =
    isUploadingServicePhoto ||
    (isLoading &&
      variables?.driverFlowStatus ===
        TaskDriverStatusEnum.TAKING_PHOTO_DROPPED_BIN);
  const isPreviousButtonLoading =
    isLoading &&
    variables?.driverFlowStatus ===
      TaskDriverStatusEnum.CATEGORIZE_BIN_CONTENTS;

  const goToPreviousStep = () => {
    updateService({
      jobId,
      serviceId,
      driverFlowStatus: TaskDriverStatusEnum.CATEGORIZE_BIN_CONTENTS,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: TaskDriverStatusEnum.CATEGORIZE_BIN_CONTENTS,
    });
  };

  const getStepNumber = () => {
    switch (taskId) {
      case ServiceTaskTypeEnum.DUMPANDRETURNBIN:
        return 2;
      case ServiceTaskTypeEnum.PICKUPBIN:
        return 3;
      case ServiceTaskTypeEnum.WAITANDLOADBIN:
        return 5;
      default:
        return 7;
    }
  };

  const stepTitle = useTranslate("driver_add_photo_bin_contents.title", {
    stepNumber: getStepNumber(),
  });

  return (
    <div className="flex flex-col h-full w-full">
      {isCameraOpen ? (
        <DriverCamera
          mode="single-photo"
          onChoosePhoto={photo => {
            setIsCameraOpen(false);
            void uploadPhoto(photo);
          }}
          onCancelClick={closeCamera}
        />
      ) : (
        <>
          <DriverTopActionButton
            jobId={jobId}
            serviceId={serviceId}
            taskId={taskId}
          />
          <div className="flex flex-grow w-full flex-col justify-center">
            <DsText variant="pageTitle">{stepTitle}</DsText>
          </div>
          <DriverFlowBottomButton
            onNextButtonClick={openCamera}
            isNextButtonLoading={isUploadingServicePhoto || isNextButtonLoading}
            nextButtonTitle="take_photo"
            previousButtonTitle="driver_add_photo_bin_contents.back_button"
            onPreviousButtonClick={
              isDumpAndReturnBinTask ? undefined : goToPreviousStep
            }
            isPreviousButtonLoading={isPreviousButtonLoading}
          />
        </>
      )}
    </div>
  );
};

export { DriverAddPhotoBinContents };
